import { setCookie, parseCookies } from 'nookies'
import { useExperiment, useInitialData, useAllowedQuery } from '../state'
import { Route } from '../config/routes'
import { ErrorCode } from '@afterpaytouch/portal-api'
import {
  isCardScanRequiredErrorCode,
  isCheckoutConfirm2FARequired,
  isCheckoutConfirmEFARequired,
  nonTerminalErrors,
  parseQueryStateToUrl,
  SHOW_NEW_CHECKOUT_COOKIE_NAME,
} from '../model'

const DEFAULT_PROCESSING_REDIRECTION_DELAY = 4000

export const browserRedirect = (destination: string, isZoidProtocol: boolean = false): void => {
  const cookies = parseCookies()
  if (!(cookies[SHOW_NEW_CHECKOUT_COOKIE_NAME] === 'true')) {
    setCookie(null, SHOW_NEW_CHECKOUT_COOKIE_NAME, JSON.stringify(true), {
      maxAge: 60 * 60, // 1 hour
      sameSite: 'lax',
      path: '/',
    })
  }

  if (isZoidProtocol && typeof window.xprops !== 'undefined') {
    window.xprops.onRecreate(destination)
    return
  }

  window.location.href = destination
}

export const useRedirectionDelay = (): number => {
  const redirectionDelay = useExperiment('ProcessingRedirectionDelay')

  if (!redirectionDelay.active) {
    return DEFAULT_PROCESSING_REDIRECTION_DELAY
  }

  switch (redirectionDelay.value) {
    case 'A':
      return 1000
    case 'B':
      return 2000
    case 'D':
      return 3000
    default:
      return DEFAULT_PROCESSING_REDIRECTION_DELAY
  }
}

export const useXbtBrowserRedirect = (): typeof browserRedirect => {
  const initialData = useInitialData()
  const isZoidProtocol = initialData?.protocol === 'zoid'

  const allowedQueryParam = useAllowedQuery()
  const queryStringToAdd = parseQueryStateToUrl(allowedQueryParam)

  return (destination: string, skipPrefill: boolean) => {
    let url = skipPrefill ? `${destination}&skipPrefill=true` : destination
    url = queryStringToAdd !== '' ? `${url}&${queryStringToAdd}` : url
    return browserRedirect(url, isZoidProtocol)
  }
}

interface ConfirmCheckoutErrorHandlingRequest {
  errorCode: ErrorCode
  returnRoute?: Route
}
interface ConfirmCheckoutErrorHandlingResponse {
  route?: Route
  messageKey?: string
}

export const getConfirmCheckoutErrorHandling = ({ errorCode, returnRoute }: ConfirmCheckoutErrorHandlingRequest): ConfirmCheckoutErrorHandlingResponse => {
  const isCardScanRequired = isCardScanRequiredErrorCode(errorCode)
  const is2FARequired = isCheckoutConfirm2FARequired(errorCode)
  const isEFARequired = isCheckoutConfirmEFARequired(errorCode)

  if (!nonTerminalErrors.includes(errorCode)) {
    return {
      route: Route.TERMINAL_ERROR,
    }
  } else if (is2FARequired || isEFARequired) {
    return {
      route: Route.VERIFY,
    }
  } else if (isCardScanRequired) {
    return {
      route: Route.CARD_SCAN,
    }
  } else if (errorCode === ErrorCode.ComplianceDeclineSystemError) {
    return {
      messageKey: 'summary:complianceDeclineSystemError',
      route: returnRoute ?? Route.TERMINAL_ERROR,
    }
  } else {
    return {}
  }
}
